<template>
  <Button
      class="checkout-btn"
      :disabled="!enabled"
      :type="!enabled ? 'secondary' : 'primary'"
      :fluid="onEstablishmentPage"
      @click="enabled ? $emit('click') : null"
  >
    {{ label }}
  </Button>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import Button from '@/components/partials/Buttons/Button/Button.vue'
import { useRoute } from "vue-router"
import { useCartStore } from "@/stores/useCartStore"
import { computed } from "vue"

const props = withDefaults(defineProps<{
  mobile?: boolean
  canOrder: boolean
}>(), {
  mobile: false,
})

const emits = defineEmits([ 'click' ])

const { t } = useI18n()
const route = useRoute()

const cartStore = useCartStore()

const enabled = computed<boolean>(() => cartStore.hasCart
    && cartStore.hasBasketInfo
    && cartStore.basketInfo?.buttons.checkout.enabled
    && props.canOrder,
)

const label = computed<string>(() => {
  if (!props.canOrder) {
    return t('checkout-button.closed')
  }

  // In case the basket has items, but does not meet the minimum amount to order
  if (cartStore.hasBasketInfo && !enabled.value) {
    return __CAPACITOR__
        ? cartStore.basketInfo?.buttons.checkout.text_short
        : cartStore.basketInfo?.buttons.checkout.text
  }

  // On the establishment page, let the user know it will go to the cart
  if (route.name.includes('establishment')) {
    return t('checkout-button.to-checkout')
  }

  // The order is either ready to be completed or the cart is empty
  // The button is either disabled or enabled
  return t('checkout-button.to-payment-provider')
})

const onEstablishmentPage = computed<boolean>(() => route.name.includes('establishment'))
</script>

<style scoped lang="scss">
@import "@/assets/css/mixins/breakpoints-down.scss";

.checkout-btn.button {
  @include lg-down {
    width: 100%;
  }

  &:deep(.button__content) {
    min-width:   16rem;
    padding-top: 0.8rem;
    padding-bottom: 0.8rem;
  }
}
</style>
